import { useMemo } from "react";
import { TopQuotes, useMessages } from "../../Services/Nats";
import { DEFAULT_PRICE_DELIMITER } from "../../price";

/**
 * The venue's price scale, as it reports it on the NATS top-of-book quotes.
 *
 * The REST depth snapshots carry integers with no scale field of their own, so the
 * delimiter is borrowed from the quote feed. That used to need a per-contract
 * lookup with two levels of fallback, because the two feeds were assumed to differ;
 * they do not. Price precision is a property of the venue, so any quote answers for
 * every contract.
 *
 * Note the top-of-book prices in the store are already converted at ingest — this
 * is only for the raw REST integers.
 */
export const useVenuePriceDelimiter = (): number => {
  const { topQuotes } = useMessages();

  return useMemo(() => {
    const delimiter = firstDelimiter(topQuotes);

    if (delimiter === undefined) {
      // Once for the app rather than once per render, so a wrong scale is visible
      // in the console instead of only in the numbers.
      console.warn(
        `Depth prices: no price_delimiter on any quote, scaling by 10^${DEFAULT_PRICE_DELIMITER}`
      );
    }

    return delimiter ?? DEFAULT_PRICE_DELIMITER;
  }, [topQuotes]);
};

const firstDelimiter = (topQuotes: TopQuotes): number | undefined => {
  for (const area in topQuotes) {
    for (const key in topQuotes[area]) {
      const delimiter = topQuotes[area][key]?.price_delimiter;
      if (delimiter !== undefined) {
        return delimiter;
      }
    }
  }
  return undefined;
};
